import { useLocation } from "react-router-dom";
import posthog from "posthog-js";
import { useAuthStore } from "@/lib/store/authStore";
import { findParentNavItem } from "@/lib/config/navigationConfig";
import type { TicketContext } from "../types";

/** Work out which record the user is viewing from the URL. */
function detectEntity(pathname: string, search: string): NonNullable<TicketContext["entity"]> {
  const params = new URLSearchParams(search);
  const detail = params.get("detail") ?? undefined;
  const seg = pathname.split("/").filter(Boolean);
  const last = seg[seg.length - 1];

  if (pathname.includes("/item")) {
    const id = detail ?? (last && last !== "item" ? last : "");
    return { type: "product", id };
  }
  if (seg[0] === "orders" && seg[1] && seg[1] !== "new") {
    return { type: "order", id: seg[1] };
  }
  if (seg[0] === "customers" && seg[1] && seg[1] !== "new") {
    return { type: "customer", id: seg[1] };
  }
  return { type: "page", id: "" };
}

/**
 * Auto-capture where/who/when the ticket is being raised from, so support can
 * jump straight to the page + PostHog session replay later. Read at submit time.
 */
export function useTicketPageContext(): TicketContext {
  const location = useLocation();
  const username = useAuthStore((s) => s.username);
  const tenantId = useAuthStore((s) => s.currentTenant);
  const company = useAuthStore((s) => s.tenantCompany);

  let sessionId: string | undefined;
  try {
    sessionId = posthog.get_session_id?.() ?? undefined;
  } catch {
    // PostHog not initialised (e.g. blocked) — session link just won't be attached.
  }

  const pageName = findParentNavItem(location.pathname)?.name;
  const entity = detectEntity(location.pathname, location.search);
  if (entity.type === "page") entity.name = pageName;

  return {
    url: location.pathname + location.search,
    pageName,
    capturedAt: new Date().toISOString(),
    userEmail: username ?? undefined,
    tenantId: tenantId ?? undefined,
    company: company ?? undefined,
    sessionId,
    entity,
  };
}
